import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  ImageOff,
  Image as ImageIcon,
  Trash2,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { formatUsd, STORES, deleteReceipt, getReceiptPhotoUrl } from '@/lib/receiptsData';
import { toast } from 'sonner';

const STORE_BADGE = {
  Menards: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  Lowes: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  'Home Depot':
    'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  Other: 'bg-[hsl(var(--muted))] text-foreground border-border',
};

const PhotoCell = ({ src, onClick }) => {
  const { t } = useTranslation();
  if (!src) {
    return (
      <div className="h-10 w-10 rounded border border-border bg-[hsl(var(--muted))]/50 flex items-center justify-center text-muted-foreground/70">
        <ImageOff size={14} />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 w-10 rounded border border-border overflow-hidden hover:ring-2 hover:ring-[hsl(var(--primary))]/40"
      aria-label={t('receiptScanner.list.viewPhotoAria')}
    >
      <img
        src={src}
        alt="Receipt"
        className="h-full w-full object-cover"
      />
    </button>
  );
};

const Row = ({ row, onDelete, onOpenJob, busy }) => {
  const { t } = useTranslation();
  const [photoOpen, setPhotoOpen] = useState(false);
  // Legacy inline photos pass through; bucket paths resolve to signed URLs.
  const [photoSrc, setPhotoSrc] = useState(row.photo || null);
  useEffect(() => {
    let alive = true;
    getReceiptPhotoUrl(row).then((url) => {
      if (alive) setPhotoSrc(url);
    });
    return () => {
      alive = false;
    };
  }, [row]);
  return (
    <>
      <div
        className="grid grid-cols-12 sm:grid-cols-[3rem_minmax(0,2.2fr)_minmax(92px,1fr)_minmax(72px,1fr)_minmax(72px,1fr)_5.5rem] gap-2 items-center px-3 py-3 border-b border-border hover:bg-[hsl(var(--muted))]/30 transition-colors"
        data-testid={`receipt-line-${row.row_key}`}
      >
        <div className="col-span-12 sm:col-span-1 flex items-center">
          <PhotoCell src={photoSrc} onClick={() => setPhotoOpen(true)} />
        </div>
        <div className="col-span-12 sm:col-span-1 min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">
            {row.job_name}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {row.client || t('receiptScanner.noClient')} {row.desc ? <span> {'\u00b7'} {row.desc}</span> : null}
          </div>
        </div>
        <div className="col-span-6 sm:col-span-1">
          <Badge
            variant="outline"
            className={cn(
              'uppercase text-[10px] tracking-[0.16em]',
              STORE_BADGE[row.store] || STORE_BADGE.Other
            )}
          >
            {row.store}
          </Badge>
          <div className="text-[11px] text-muted-foreground mt-1">{row.date || t('receiptScanner.list.unknownDate')}</div>
        </div>
        <div className="col-span-3 sm:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('receiptScanner.list.colAmount')}
          </div>
          <div className="text-sm font-semibold text-foreground tabular-nums">
            {formatUsd(row.amount)}
          </div>
        </div>
        <div className="col-span-3 sm:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('receiptScanner.list.colRebate')}
          </div>
          <div className="text-sm font-semibold text-[hsl(var(--success))] tabular-nums">
            {formatUsd(row.rebate)}
          </div>
        </div>
        <div className="col-span-12 sm:col-span-1 flex items-center justify-end gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onOpenJob(row)}
            className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
            aria-label={t('receiptScanner.list.openJobAria')}
            data-testid={`receipt-row-open-job-${row.row_key}`}
          >
            <ExternalLink size={14} />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onDelete(row)}
            disabled={busy}
            className="h-8 w-8 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
            aria-label={t('receiptScanner.list.removeAria')}
            data-testid={`receipt-row-delete-${row.row_key}`}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
          </Button>
        </div>
      </div>

      {photoOpen && photoSrc ? (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setPhotoOpen(false)}
          data-testid={`receipt-photo-modal-${row.row_key}`}
        >
          <img
            src={photoSrc}
            alt={`Receipt for ${row.job_name}`}
            className="max-h-[90vh] max-w-[90vw] rounded-md shadow-2xl border border-border"
          />
        </div>
      ) : null}
    </>
  );
};

const ListSkeleton = () => (
  <div className="divide-y divide-border" data-testid="receipts-skeleton">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="px-3 py-4 flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20 ml-auto" />
      </div>
    ))}
  </div>
);

export const ReceiptsList = ({
  rows,
  loading,
  storeFilter,
  onStoreFilter,
  jobFilter,
  onJobFilter,
  jobsForFilter,
  search,
  onSearch,
  onChanged,
  jobs,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);

  const handleDelete = async (row) => {
    const job = jobs.find((j) => j.id === row.job_id);
    if (!job) return;
    setBusyId(row.row_key);
    try {
      const updated = await deleteReceipt(job, row.receipt_id);
      toast.success(t('receiptScanner.list.receiptRemoved'));
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.message || t('receiptScanner.list.removeFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenJob = (row) => {
    if (!row?.job_id) return;
    navigate(`/dashboard/active-jobs?jobId=${encodeURIComponent(row.job_id)}`);
  };

  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="receipts-table-card"
    >
      <CardContent className="p-3 sm:p-4">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3 mb-3">
          <Select value={storeFilter} onValueChange={onStoreFilter}>
            <SelectTrigger
              className="h-9 w-full sm:w-[180px] bg-[hsl(var(--muted))] border-border"
              data-testid="receipts-store-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-border">
              <SelectItem value="all">{t('receiptScanner.list.allStores')}</SelectItem>
              {STORES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={jobFilter} onValueChange={onJobFilter}>
            <SelectTrigger
              className="h-9 w-full sm:w-[260px] bg-[hsl(var(--muted))] border-border"
              data-testid="receipts-job-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
              <SelectItem value="all">{t('receiptScanner.list.allJobs')}</SelectItem>
              {(jobsForFilter || []).map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-1 lg:min-w-[220px] items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={14}
              />
              <Input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t('receiptScanner.list.searchPlaceholder')}
                className="pl-8 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="receipts-search"
              />
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="hidden sm:grid sm:grid-cols-[3rem_minmax(0,2.2fr)_minmax(92px,1fr)_minmax(72px,1fr)_minmax(72px,1fr)_5.5rem] gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span className="sr-only">{t('receiptScanner.list.colPhoto')}</span>
          <span className="col-start-2">{t('receiptScanner.list.colJobClient')}</span>
          <span>{t('receiptScanner.list.colStore')}</span>
          <span>{t('receiptScanner.list.colAmount')}</span>
          <span>{t('receiptScanner.list.colRebate')}</span>
          <span className="text-right">{t('receiptScanner.list.colActions')}</span>
        </div>

        {loading ? (
          <ListSkeleton />
        ) : rows.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2"
            data-testid="receipts-empty"
          >
            <ImageIcon size={20} className="text-muted-foreground/70" />
            {t('receiptScanner.list.noReceipts')}
          </div>
        ) : (
          <div data-testid="receipts-list">
            {rows.map((row) => (
              <Row
                key={row.row_key}
                row={row}
                jobs={jobs}
                onDelete={handleDelete}
                onOpenJob={handleOpenJob}
                busy={busyId === row.row_key}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReceiptsList;
